import { connectorIsReady } from "@/lib/channel-coverage";
import { providerLabel } from "@/lib/listing-action";

export type ConnectorIdentityInput = {
  id: string;
  name: string;
  provider: string | null;
  authStatus?: string | null;
  storeName?: string | null;
  storeHash?: string | null;
  storefrontOrigin?: string | null;
  sellerUsername?: string | null;
};

function hostFromOrigin(origin: string | null | undefined): string | null {
  if (!origin) return null;
  try {
    const host = new URL(origin).hostname.replace(/^www\./, "");
    return host || null;
  } catch {
    return null;
  }
}

/**
 * Operator-facing channel identity. Storefront connectors use store identity
 * (not an internal nickname such as "Borraq"). Account connectors keep their
 * account label. Future providers fall through to provider + name.
 */
export function connectorDisplayName(c: ConnectorIdentityInput): string {
  const provider = providerLabel(c.provider);
  if (c.provider === "bigcommerce") {
    const store =
      (c.storeName && c.storeName.trim()) ||
      hostFromOrigin(c.storefrontOrigin) ||
      (c.storeHash ? c.storeHash : "");
    return store ? `${provider} · ${store}` : provider || c.name;
  }
  if (c.provider === "ebay") {
    const account = (c.name && c.name.trim()) || (c.sellerUsername && c.sellerUsername.trim());
    return account || provider;
  }
  const name = c.name?.trim();
  if (name && provider && name.toLowerCase() !== provider.toLowerCase()) {
    return `${provider} · ${name}`;
  }
  return name || provider || "Channel";
}

export function connectorAuthLabel(authStatus: string | null | undefined): string {
  return connectorIsReady(authStatus) ? "Authorized" : "Not authorized";
}

export function formatConnectorIdentityLine(c: ConnectorIdentityInput): string {
  return `${connectorDisplayName(c)} — ${connectorAuthLabel(c.authStatus)}`;
}

export function connectorReadyForDrafts(authStatus: string | null | undefined): boolean {
  return connectorIsReady(authStatus);
}
